import { createContext, useEffect, type ReactNode } from "react";
import { css, Global } from "@emotion/react";

const DesignSystemContext = createContext(null);

type DesignSystemProviderProps = {
  children: ReactNode;
};

export function DesignSystemProvider(props: DesignSystemProviderProps) {
  const { children } = props;

  // NOTE: it's slightly less performant to inject the "link" tags here vs. hardcoding them in
  // the project's HTML since JS has to be downloaded and parsed before the browser has an opportunity
  // to request resources from this link but it does mean we can encapsulate the font implementation
  // <link rel="preconnect" href="https://fonts.googleapis.com" />
  // <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
  useEffect(() => {
    // add preconnect links to Google Fonts
    const googleFontsLink = document.createElement("link");
    googleFontsLink.rel = "preconnect";
    googleFontsLink.href = "https://fonts.googleapis.com";

    const gstaticLink = document.createElement("link");
    gstaticLink.rel = "preconnect";
    gstaticLink.href = "https://fonts.gstatic.com";
    gstaticLink.crossOrigin = "anonymous";

    document.head.appendChild(googleFontsLink);
    document.head.appendChild(gstaticLink);

    // remove preconnect links when component unmounts
    return () => {
      document.head.removeChild(googleFontsLink);
      document.head.removeChild(gstaticLink);
    };
  }, []);

  return (
    <DesignSystemContext.Provider value={null}>
      <GlobalStyles />
      {children}
    </DesignSystemContext.Provider>
  );
}

const GlobalStyles = () => (
  /**
   * 1) import Poppins font from Google Fonts
   *    - not using `@font-face` as we are importing fonts directly from Google Fonts URL
   * 2) override browser default font-weights for Poppins
   *    - our design system font includes the following font-weights: 400, 500 and 600. Therefore,
   *     the browser will attempt to workaround this when font keywords are used, such as
   *     `font-weight: bold`, which is typically represented as 700 and `heavy` as 900.
   *     We want to override these browser defaults to explicitly use our font weight values,
   *     such as `font-weight: bold` to be represented as 600 whenever this keyword style is used.
   */

  <Global
    styles={css`
      @import url("https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,400;0,500;0,600;1,400;1,600&display=swap");

      [style*="font-weight: normal"] {
        font-weight: var(--swing-font-normal) !important;
      }

      [style*="font-weight: bold"],
      [style*="font-weight: heavy"] {
        font-weight: var(--swing-font-semibold) !important;
      }
    `}
  />
);
